import {
  put,
  delay,
  takeEvery,
  all,
  call,
  select,
  race,
  cancelled,
} from "redux-saga/effects";
import _ from "lodash";
import moment from "moment-timezone";
import {
  setPageTopLoader,
  setByAreaDataSummaryTepco,
  setByAreaDataSummaryNtt,
  setByAreaDataSummaryNttFt,
  setByAreaDataSummaryHokuden,
  setByAreaDataTableTepco,
  setByAreaDataTableNtt,
  setByAreaDataRowsNtt,
  setByAreaDataTableNttFt,
  setByAreaDataTableHokuden,
  setByAreaTrendDataLoadingNtt,
  setByAreaTrendDataNtt,
} from "actions";
import { getJwtToken, getDataFromRDS, getDataFromSxi } from "api";
import { PICTREE, ENDPOINT } from "../../constants";
import {
  REGULAR_SEASON_IDS,
  isRegularSeasonId,
} from "../../constants/pictreeRegularSeasonIds";

const AREA_BATCH_SUPPORTED_SEASON_IDS = new Set(
  String(
    process.env.REACT_APP_PICTREE_AREA_BATCH_SUPPORTED_SEASON_IDS ||
      REGULAR_SEASON_IDS.join(",")
  )
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean)
);
const REGULAR_SEASON_AREA_SNAPSHOT_TABLE =
  process.env.REACT_APP_PICTREE_AREA_SNAPSHOT_TABLE ||
  (process.env.NODE_ENV === "development"
    ? "PicTreeKpiRegularSeasonAreaSnapshotProd"
    : "PicTreeKpiRegularSeasonAreaSnapshotProd");

const REGULAR_SEASON_AREA_SNAPSHOT_COLUMNS = [
  "name",
  "count",
  "reviewok_count",
  "achieved_percent",
  "participants_count",
  "total_participants",
  "medical_area_name",
  "medical_area_id",
  "medical_area_code",
  "city_name",
  "city_area_id",
  "city_area_code",
  "prefecture_name",
  "prefecture_area_id",
  "prefecture_code",
];

const REGULAR_SEASON_AREA_SNAPSHOT_SUMMARY_COLUMNS = [
  "registered_area_count",
  "unshot_area_count",
  "total_registered",
  "total_review_ok",
  "total_achieved_percent",
  "total_participants",
];

const REGULAR_SEASON_AREA_TREND_COLUMNS = [
  "area_name",
  "snapshot_date_jst",
  "achieved_percent",
  "registered_count",
  "reviewok_count",
  "participants_count",
  "total_participants",
  "prefecture_name",
];

const RDS_HEAVY_RETRY = {
  timeoutMs: 30000, // 30s
  retries: 3, // 合計 4 回までトライ（初回  リトライ3）
  backoffBase: 1500, // 1.5s指数バックオフ
};

const REGULAR_AREA_TABLE_DEFAULT_LIMIT = 200;

function escapeSqlLiteral(value) {
  return String(value || "")
    .replace(/([\\%_])/g, "\\$1")
    .replace(/'/g, "''");
}

function normalizeAreaTableLimit(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return REGULAR_AREA_TABLE_DEFAULT_LIMIT;
  }
  return Math.min(Math.floor(parsed), REGULAR_AREA_TABLE_DEFAULT_LIMIT);
}

function normalizeAreaTableSortDirection(value) {
  return String(value || "DESC").toUpperCase() === "ASC" ? "ASC" : "DESC";
}

function buildAreaTableSearchClause(searchText) {
  const keyword = String(searchText || "").trim();
  if (!keyword) {
    return "";
  }

  const escaped = escapeSqlLiteral(keyword);
  return `
          AND (
            areaName LIKE '%${escaped}%'
            OR prefectureName LIKE '%${escaped}%'
            OR medicalAreaName LIKE '%${escaped}%'
            OR cityName LIKE '%${escaped}%'
          )`;
}

// タイムアウト＋リトライ
function* callWithTimeoutAndRetry(fn, args = [], opts = {}) {
  const {
    timeoutMs = 20000, // 1回の待ち時間（APIGW上限より少し短く）
    retries = 2, // 失敗時の再試行回数（合計3回＝初回＋2リトライ）
    backoffBase = 1000, // 1秒
    backoffFactor = 2, // 2倍ずつ
    jitter = true, // 少しランダムに待つ
    isRetryable = (err) => {
      const msg = (err && (err.message || err.errorMessage)) || "";
      return /timeout|timed out|504|gateway\s*time-?out/i.test(msg);
    },
  } = opts;

  let attempt = 0;
  while (true) {
    attempt += 1;

    try {
      // 1回の試行にタイムアウトをかける
      const { response, timeout } = yield race({
        response: call(fn, ...args),
        timeout: delay(timeoutMs),
      });

      if (timeout) throw new Error(`timeout after ${timeoutMs}ms`);
      return response; // 成功
    } catch (err) {
      if (attempt > retries || !isRetryable(err)) {
        throw err; // もう無理/リトライ不要
      }
      // 待ってから再試行（指数＋ジッター）
      const wait = Math.min(
        backoffBase * Math.pow(backoffFactor, attempt - 1),
        10000
      );
      const jitterMs = jitter ? Math.floor(Math.random() * (wait * 0.3)) : 0;
      yield delay(wait + jitterMs);
      // 次のループで再試行
    } finally {
      if (yield cancelled()) {
        // もし fetch/axios なら AbortController を仕込むとさらに良い
      }
    }
  }
}

function* runEffectsInBatches(effects, { concurrency = 2, gapMs = 300 } = {}) {
  const results = [];
  for (let i = 0; i < effects.length; i += concurrency) {
    const batch = effects.slice(i, i + concurrency);
    // 同時に投げるのは batch サイズ分だけ
    const batchResults = yield all(batch);
    results.push(...batchResults);

    // 次バッチの前にクールダウン（最後のバッチは不要）
    if (i + concurrency < effects.length && gapMs > 0) {
      yield delay(gapMs);
    }
  }
  return results;
}

function parseRdsRows(response, columns) {
  const body = response && response.body ? response.body : "[]";
  const parsed = typeof body === "string" ? JSON.parse(body) : body;

  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed.map((row) => {
    if (!Array.isArray(row)) {
      return row;
    }

    return columns.reduce((acc, column, index) => {
      const cell = row[index];
      if (cell && typeof cell === "object") {
        if (cell.isNull) {
          acc[column] = null;
        } else if (Object.prototype.hasOwnProperty.call(cell, "longValue")) {
          acc[column] = cell.longValue;
        } else if (Object.prototype.hasOwnProperty.call(cell, "doubleValue")) {
          acc[column] = cell.doubleValue;
        } else if (Object.prototype.hasOwnProperty.call(cell, "stringValue")) {
          acc[column] = cell.stringValue;
        } else {
          acc[column] = null;
        }
      } else {
        acc[column] = cell ?? null;
      }
      return acc;
    }, {});
  });
}

function buildDateArray(startDate, endDate) {
  if (!startDate || !endDate) {
    return [];
  }

  const start = moment.tz(startDate, "Asia/Tokyo").startOf("day");
  const end = moment.tz(endDate, "Asia/Tokyo").startOf("day");
  const result = [];

  if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
    return result;
  }

  const cursor = start.clone();
  while (cursor.isSameOrBefore(end)) {
    result.push(cursor.format("YYYY-MM-DD"));
    cursor.add(1, "day");
  }

  return result;
}

function hashColor(label) {
  const text = String(label || "");
  const hash = Array.from(text).reduce(
    (acc, char) => (acc * 31 + char.charCodeAt(0)) >>> 0,
    0
  );
  const hue = hash % 360;
  return `hsl(${hue}, 70%, 56%)`;
}

function buildTrendPayload(
  rows,
  startDate,
  endDate,
  gameFieldLayerId,
  sortDirection = "DESC"
) {
  const dateArray = buildDateArray(startDate, endDate);
  const areaMap = new Map();

  rows.forEach((row) => {
    const areaNameAdjusted =
      gameFieldLayerId !== "regular_1_l1" ? `${row.area_name} (${row.prefecture_name})` : row.area_name;
    const areaName = areaNameAdjusted || "";
    const snapshotDate = row.snapshot_date_jst || row.snapshotDateJst || "";
    if (!areaName || !snapshotDate) {
      return;
    }

    if (!areaMap.has(areaName)) {
      areaMap.set(areaName, {
        name: areaName,
        values: {},
        latestValue: 0,
      });
    }

    const current = areaMap.get(areaName);
    const achieved = Number(row.achieved_percent ?? row.achievedPercent ?? 0) || 0;
    current.values[snapshotDate] = achieved;
    current.latestValue = achieved;
  });

  const series = Array.from(areaMap.values())
    .map((item) => {
      const data = dateArray.map((date) => Number(item.values[date] ?? 0));
      const lastNonZero = [...data].reverse().find((value) => value !== 0) ?? 0;
      const latestValue = lastNonZero || data[data.length - 1] || 0;

      return {
        name: item.name,
        data,
        latestValue,
        color: hashColor(item.name),
      };
    })
    .sort((a, b) => {
      if (a.latestValue !== b.latestValue) {
        return sortDirection === "ASC"
          ? a.latestValue - b.latestValue
          : b.latestValue - a.latestValue;
      }
      return a.name.localeCompare(b.name, "ja");
    });

  return {
    date: dateArray,
    series,
    // topLegendNames: series.slice(0, 30).map((item) => item.name),
    topLegendNames: series.map((item) => item.name),
  };
}

const createApprovedByAreaQuery = ({
  startUtc,
  endUtc,
  seasonId,
  gameFieldLayerId,
  snapshotEndDateJst,
  useAreaSnapshotTable = false,
  searchText = "",
  rowsPerPage = REGULAR_AREA_TABLE_DEFAULT_LIMIT,
  sortDirection = "DESC",
  applyTableLimit = false,
}) => {
  if (useAreaSnapshotTable && gameFieldLayerId) {
    const searchClause = applyTableLimit
      ? buildAreaTableSearchClause(searchText)
      : "";
    const limitClause = applyTableLimit
      ? `LIMIT ${normalizeAreaTableLimit(rowsPerPage)}`
      : "";
    const orderByDirection = applyTableLimit
      ? normalizeAreaTableSortDirection(sortDirection)
      : "DESC";
    return {
      query: `
        SELECT
          areaName AS name,
          registeredCount AS count,
          reviewOkCount AS reviewok_count,
          achievedPercent AS achieved_percent,
          participantsCount AS participants_count,
          totalParticipants AS total_participants,
          medicalAreaName AS medical_area_name,
          medicalAreaId AS medical_area_id,
          medicalAreaCode AS medical_area_code,
          cityAreaName AS city_name,
          cityAreaId AS city_area_id,
          cityAreaCode AS city_area_code,
          prefectureName AS prefecture_name,
          prefectureAreaId AS prefecture_area_id,
          prefectureCode AS prefecture_code
        FROM ${REGULAR_SEASON_AREA_SNAPSHOT_TABLE}
        WHERE seasonId = '${seasonId}'
          AND companyCode = 'NTT_ME'
          AND areaLayerId = '${gameFieldLayerId}'
          AND snapshotDateJst = '${snapshotEndDateJst}'
${searchClause}
        ORDER BY achievedPercent ${orderByDirection}, registeredCount DESC, areaName ASC
        ${limitClause};
      `,
      database: "KpiDashboard",
      type: "list",
    };
  }

  if (gameFieldLayerId === "regular_1_l2") {
    return {
      query: `
        WITH season AS (
          SELECT id, game_field_id
          FROM game_space
          WHERE id = '${seasonId}'
        ),
        area_map AS (
          SELECT
            gfab.game_field_area_id AS area_id
          FROM season s
          JOIN game_field_layer_bind gflb
            ON gflb.game_field_id = s.game_field_id
          JOIN game_field_area_bind gfab
            ON gfab.game_field_layer_id = gflb.game_field_layer_id
          WHERE gflb.game_field_layer_id = '${gameFieldLayerId}'
        ),
        asset_area AS (
          SELECT DISTINCT
            paga.power_asset_id,
            paga.area_id
          FROM power_asset_game_space_area paga
          WHERE paga.game_space_id = '${seasonId}'
        ),
        approved_rr AS (
          SELECT
            rr.power_asset_id,
            rr.player_id
          FROM review_request_event rre
          JOIN review_request rr
            ON rr.id = rre.review_request_id
          WHERE rre.type = 'APPROVED'
            AND rre.occurred_datetime >= '${startUtc}'
            AND rre.occurred_datetime <  '${endUtc}'
            AND rre.game_space_id = '${seasonId}'
          GROUP BY rr.power_asset_id, rr.player_id
        ),
        medical_enriched AS (
          SELECT
            med.id AS medical_area_id,
            med.area_code AS medical_area_code,
            med.superior_area_id AS prefecture_area_id,
            MIN(med_name.name) AS medical_area_name,
            pref.area_code AS prefecture_code,
            MIN(pref_name.name) AS prefecture_name
          FROM game_field_area med
          JOIN area_map am
            ON am.area_id = med.id
          LEFT JOIN game_field_area_display_name med_name
            ON med_name.area_id = med.id
           AND med_name.language_code = 'JA'
          LEFT JOIN game_field_area pref
            ON pref.id = med.superior_area_id
          LEFT JOIN game_field_area_display_name pref_name
            ON pref_name.area_id = pref.id
           AND pref_name.language_code = 'JA'
          GROUP BY
            med.id,
            med.area_code,
            med.superior_area_id,
            pref.area_code
        ),
        global_participants AS (
          SELECT COUNT(DISTINCT player_id) AS total_participants
          FROM approved_rr
        )
        SELECT
          me.medical_area_id,
          me.medical_area_name,
          me.medical_area_code,
          me.prefecture_area_id,
          me.prefecture_name,
          me.prefecture_code,
          COUNT(DISTINCT aa.power_asset_id) AS registered_count,
          COUNT(DISTINCT ar.power_asset_id) AS reviewok_count,
          ROUND(
            CASE
              WHEN COUNT(DISTINCT aa.power_asset_id) = 0 THEN 0
              ELSE COUNT(DISTINCT ar.power_asset_id) / COUNT(DISTINCT aa.power_asset_id) * 100
            END,
            2
          ) AS achieved_percent,
          COUNT(DISTINCT ar.player_id) AS participants_count,
          gp.total_participants AS total_participants
        FROM medical_enriched me
        LEFT JOIN asset_area aa
          ON aa.area_id = me.medical_area_id
        LEFT JOIN approved_rr ar
          ON ar.power_asset_id = aa.power_asset_id
        CROSS JOIN global_participants gp
        GROUP BY
          me.medical_area_id,
          me.medical_area_name,
          me.medical_area_code,
          me.prefecture_area_id,
          me.prefecture_name,
          me.prefecture_code,
          gp.total_participants
        ORDER BY registered_count DESC, me.medical_area_name ASC;
      `,
      database: "KpiDashboard",
      type: "list",
    };
  }

  if (gameFieldLayerId === "regular_1_l3") {
    return {
      query: `
        WITH season AS (
          SELECT id, game_field_id
          FROM game_space
          WHERE id = '${seasonId}'
        ),
        area_map AS (
          SELECT
            gfab.game_field_area_id AS area_id
          FROM season s
          JOIN game_field_layer_bind gflb
            ON gflb.game_field_id = s.game_field_id
          JOIN game_field_area_bind gfab
            ON gfab.game_field_layer_id = gflb.game_field_layer_id
          WHERE gflb.game_field_layer_id = '${gameFieldLayerId}'
        ),
        asset_area AS (
          SELECT DISTINCT
            paga.power_asset_id,
            paga.area_id
          FROM power_asset_game_space_area paga
          WHERE paga.game_space_id = '${seasonId}'
        ),
        approved_rr AS (
          SELECT
            rr.power_asset_id,
            rr.player_id
          FROM review_request_event rre
          JOIN review_request rr
            ON rr.id = rre.review_request_id
          WHERE rre.type = 'APPROVED'
            AND rre.occurred_datetime >= '${startUtc}'
            AND rre.occurred_datetime <  '${endUtc}'
            AND rre.game_space_id = '${seasonId}'
          GROUP BY rr.power_asset_id, rr.player_id
        ),
        city_enriched AS (
          SELECT
            city.id AS city_area_id,
            city.area_code AS city_area_code,
            city.superior_area_id AS medical_area_id,
            city_name.name AS city_name,
            med.superior_area_id AS prefecture_area_id,
            pref.area_code AS prefecture_code,
            pref_name.name AS prefecture_name
          FROM game_field_area city
          JOIN area_map am
            ON am.area_id = city.id
          LEFT JOIN game_field_area_display_name city_name
            ON city_name.area_id = city.id
           AND city_name.language_code = 'JA'
          LEFT JOIN game_field_area med
            ON med.id = city.superior_area_id
          LEFT JOIN game_field_area pref
            ON pref.id = med.superior_area_id
          LEFT JOIN game_field_area_display_name pref_name
            ON pref_name.area_id = pref.id
           AND pref_name.language_code = 'JA'
        ),
        global_participants AS (
          SELECT COUNT(DISTINCT player_id) AS total_participants
          FROM approved_rr
        )
        SELECT
          ce.city_area_id,
          ce.city_name,
          ce.city_area_code,
          ce.prefecture_area_id,
          ce.prefecture_name,
          ce.prefecture_code,
          COUNT(DISTINCT aa.power_asset_id) AS registered_count,
          COUNT(DISTINCT ar.power_asset_id) AS reviewok_count,
          ROUND(
            CASE
              WHEN COUNT(DISTINCT aa.power_asset_id) = 0 THEN 0
              ELSE COUNT(DISTINCT ar.power_asset_id) / COUNT(DISTINCT aa.power_asset_id) * 100
            END,
            2
          ) AS achieved_percent,
          COUNT(DISTINCT ar.player_id) AS participants_count,
          gp.total_participants AS total_participants
        FROM city_enriched ce
        LEFT JOIN asset_area aa
          ON aa.area_id = ce.city_area_id
        LEFT JOIN approved_rr ar
          ON ar.power_asset_id = aa.power_asset_id
        CROSS JOIN global_participants gp
        GROUP BY
          ce.city_area_id,
          ce.city_name,
          ce.city_area_code,
          ce.prefecture_area_id,
          ce.prefecture_name,
          ce.prefecture_code,
          gp.total_participants
        ORDER BY registered_count DESC, ce.city_name ASC;
      `,
      database: "KpiDashboard",
      type: "list",
    };
  }

  if (gameFieldLayerId) {
    return {
      query: `
        WITH season AS (
          SELECT id, game_field_id
          FROM game_space
          WHERE id = '${seasonId}'
        ),
        area_map AS (
          SELECT
            gfab.game_field_area_id AS area_id,
            MIN(adn.name) AS area_name
          FROM season s
          JOIN game_field_layer_bind gflb
            ON gflb.game_field_id = s.game_field_id
          JOIN game_field_area_bind gfab
            ON gfab.game_field_layer_id = gflb.game_field_layer_id
          JOIN game_field_area_display_name adn
            ON adn.area_id = gfab.game_field_area_id
           AND adn.language_code = 'JA'
          WHERE gflb.game_field_layer_id = '${gameFieldLayerId}'
          GROUP BY gfab.game_field_area_id
        ),
        asset_area AS (
          SELECT DISTINCT
            paga.power_asset_id,
            paga.area_id
          FROM power_asset_game_space_area paga
          WHERE paga.game_space_id = '${seasonId}'
        ),
        approved_rr AS (
          SELECT
            rr.power_asset_id,
            rr.player_id
          FROM review_request_event rre
          JOIN review_request rr
            ON rr.id = rre.review_request_id
          WHERE rre.type = 'APPROVED'
            AND rre.occurred_datetime >= '${startUtc}'
            AND rre.occurred_datetime <  '${endUtc}'
            AND rre.game_space_id = '${seasonId}'
          GROUP BY rr.power_asset_id, rr.player_id
        ),
        per_area_registered AS (
          SELECT am.area_name, COUNT(DISTINCT aa.power_asset_id) AS registered_count
          FROM asset_area aa
          JOIN area_map am
            ON am.area_id = aa.area_id
          GROUP BY am.area_name
        ),
        per_area_reviewok AS (
          SELECT am.area_name, COUNT(DISTINCT ar.power_asset_id) AS reviewok_count
          FROM approved_rr ar
          JOIN asset_area aa
            ON aa.power_asset_id = ar.power_asset_id
          JOIN area_map am
            ON am.area_id = aa.area_id
          GROUP BY am.area_name
        ),
        per_area_participants AS (
          SELECT am.area_name, COUNT(DISTINCT ar.player_id) AS participants_count
          FROM approved_rr ar
          JOIN asset_area aa
            ON aa.power_asset_id = ar.power_asset_id
          JOIN area_map am
            ON am.area_id = aa.area_id
          GROUP BY am.area_name
        ),
        global_participants AS (
          SELECT COUNT(DISTINCT player_id) AS total_participants
          FROM approved_rr
        )
        SELECT
          r.area_name AS name,
          r.registered_count AS count,
          COALESCE(o.reviewok_count, 0) AS reviewok_count,
          ROUND(COALESCE(o.reviewok_count, 0) / r.registered_count * 100, 2) AS achieved_percent,
          COALESCE(p.participants_count, 0) AS participants_count,
          gp.total_participants AS total_participants
        FROM per_area_registered r
        LEFT JOIN per_area_reviewok o
          ON o.area_name = r.area_name
        LEFT JOIN per_area_participants p
          ON p.area_name = r.area_name
        CROSS JOIN global_participants gp
        ORDER BY r.registered_count DESC, r.area_name ASC;
      `,
      database: "KpiDashboard",
      type: "list",
    };
  }

  return {
    query: `
      WITH assets AS (
        SELECT /*+ SET_VAR(sort_buffer_size=262144) */
              DISTINCT pas.power_asset_id
        FROM power_asset_state pas
        WHERE pas.game_space_id = '${seasonId}'
      ),

      -- 代表エリア名をウィンドウ関数なしで確定（最小名を代表に）
      area_map AS (
        SELECT
          paga.power_asset_id,
          MIN(n.name) AS area_name
        FROM power_asset_game_space_area paga
        JOIN game_space_area_name n
          ON n.area_id = paga.area_id
        AND n.language_code = 'JA'
        JOIN assets a
          ON a.power_asset_id = paga.power_asset_id
        WHERE paga.game_space_id = '${seasonId}'
        GROUP BY paga.power_asset_id
      ),

      -- 承認付き RR を先に「資産×プレイヤ」で圧縮
      approved_rr AS (
        SELECT
          rr.power_asset_id,
          rr.player_id
        FROM review_request_event rre
        JOIN review_request rr
          ON rr.id = rre.review_request_id
        WHERE rre.type = 'APPROVED'
          AND rre.occurred_datetime >= '${startUtc}'
          AND rre.occurred_datetime <  '${endUtc}'
          AND rre.game_space_id = '${seasonId}'
        GROUP BY rr.power_asset_id, rr.player_id
      ),

      -- 登録（資産数）
      per_area_registered AS (
        SELECT am.area_name, COUNT(*) AS registered_count
        FROM area_map am
        GROUP BY am.area_name
      ),

      -- レビューOK（ユニーク資産）
      per_area_reviewok AS (
        SELECT am.area_name, COUNT(DISTINCT ar.power_asset_id) AS reviewok_count
        FROM approved_rr ar
        JOIN area_map am ON am.power_asset_id = ar.power_asset_id
        GROUP BY am.area_name
      ),

      -- 参加者（エリア内ユニークプレイヤ）
      per_area_participants AS (
        SELECT am.area_name, COUNT(DISTINCT ar.player_id) AS participants_count
        FROM approved_rr ar
        JOIN area_map am ON am.power_asset_id = ar.power_asset_id
        GROUP BY am.area_name
      ),

      -- 全体ユニーク参加者（エリア跨ぎ重複排除）
      global_participants AS (
        SELECT COUNT(DISTINCT player_id) AS total_participants
        FROM approved_rr
      )

      SELECT
        r.area_name AS name,
        r.registered_count AS count,
        COALESCE(o.reviewok_count, 0) AS reviewok_count,
        ROUND(COALESCE(o.reviewok_count, 0) / r.registered_count * 100, 2) AS achieved_percent,
        COALESCE(p.participants_count, 0) AS participants_count,
        gp.total_participants AS total_participants
      FROM per_area_registered r
      LEFT JOIN per_area_reviewok o  ON o.area_name = r.area_name
      LEFT JOIN per_area_participants p ON p.area_name = r.area_name
      CROSS JOIN global_participants gp
      ORDER BY r.registered_count DESC, r.area_name ASC;
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const createApprovedByAreaSummaryQuery = ({
  seasonId,
  gameFieldLayerId,
  snapshotEndDateJst,
}) => ({
  query: `
    SELECT
      COUNT(*) AS registered_area_count,
      COUNT(CASE WHEN reviewOkCount = 0 THEN 1 END) AS unshot_area_count,
      COALESCE(SUM(registeredCount), 0) AS total_registered,
      COALESCE(SUM(reviewOkCount), 0) AS total_review_ok,
      CASE
        WHEN COALESCE(SUM(registeredCount), 0) = 0 THEN 0
        ELSE ROUND(
          COALESCE(SUM(reviewOkCount), 0) / COALESCE(SUM(registeredCount), 0) * 100,
          2
        )
      END AS total_achieved_percent,
      COALESCE(MAX(totalParticipants), 0) AS total_participants
    FROM ${REGULAR_SEASON_AREA_SNAPSHOT_TABLE}
    WHERE seasonId = '${seasonId}'
      AND companyCode = 'NTT_ME'
      AND areaLayerId = '${gameFieldLayerId}'
      AND snapshotDateJst = '${snapshotEndDateJst}';
  `,
  database: "KpiDashboard",
  type: "list",
});

export const currentPage = (state) => state.page.currentPage;
export const dateRange = (state) => state.page.dateRangePicTree;
export const dateRangeNtt = (state) => state.page.dateRangePicTreeNtt;
export const dateRangeNttFt = (state) => state.page.dateRangePicTreeNttFt;
export const dateRangeHokuden = (state) => state.page.dateRangePicTreeHokuden;
export const selectedSeason = (state) => state.pictree.selectedSeason;
export const selectedSeasonNtt = (state) => state.pictreeNtt.selectedSeason;
export const selectedAreaLayerIdNtt = (state) => state.pictreeNtt.selectedAreaLayerId;
export const selectedSeasonNttFt = (state) => state.pictreeNttFt.selectedSeason;
export const selectedSeasonHokuden = (state) => state.pictreeHokuden.selectedSeason;

export function* getByAreaData(action) {
  try {
    yield put(setPageTopLoader(true));
    yield put(setByAreaDataSummaryTepco([]));
    yield put(setByAreaDataTableTepco([]));
    yield put(setByAreaDataSummaryNtt([]));
    yield put(setByAreaDataRowsNtt([]));
    yield put(setByAreaDataSummaryNttFt([]));
    yield put(setByAreaDataTableNttFt([]));
    yield put(setByAreaDataSummaryHokuden([]));
    yield put(setByAreaDataTableHokuden([]));

    const jwtToken = yield call(getJwtToken);
    const request =
      typeof action.payload === "string"
        ? { company: action.payload }
        : action.payload || {};
    const targetCompany =
      typeof action.payload === "string" ? action.payload : request.company || "";
    let selectedSeasonData = [];
    let rangeTarget = "";
    let seasonId = "";

    if (targetCompany === "tepco") {
      rangeTarget = yield select(dateRange);
      selectedSeasonData = yield select(selectedSeason);

    } else if (targetCompany === "ntt") {
      rangeTarget = yield select(dateRangeNtt);
      selectedSeasonData = yield select(selectedSeasonNtt);
    } else if (targetCompany === "ntt-ft") {
      rangeTarget = yield select(dateRangeNttFt);
      selectedSeasonData = yield select(selectedSeasonNttFt);
    } else if (targetCompany === "hokuden") {
      rangeTarget = yield select(dateRangeHokuden);
      selectedSeasonData = yield select(selectedSeasonHokuden);
    }

    if (!selectedSeasonData || selectedSeasonData.length === 0) {
      if (targetCompany === "ntt") {
        const seasonDataFallback = yield select((state) => state.pictreeNtt.seasonData);
        if (seasonDataFallback && seasonDataFallback.length > 0) {
          selectedSeasonData = [seasonDataFallback[0]];
        }
      }
    }

    if (!selectedSeasonData || selectedSeasonData.length === 0) {
      console.warn("[getByAreaData] no selected season data", targetCompany);
      yield put(setPageTopLoader(false));
      return;
    }

    seasonId = selectedSeasonData[0].seasonId;
    const gameFieldLayerId =
      targetCompany === "ntt" && AREA_BATCH_SUPPORTED_SEASON_IDS.has(seasonId)
        ? (yield select(selectedAreaLayerIdNtt)) || "regular_1_l1"
        : null;
    const useAreaSnapshotTable =
      targetCompany === "ntt" &&
      AREA_BATCH_SUPPORTED_SEASON_IDS.has(seasonId) &&
      Boolean(gameFieldLayerId);

    // JST日の境界 → UTC へ
    const startUtc = moment
      .tz(`${rangeTarget[0]} 00:00:00`, "Asia/Tokyo")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    const endUtc = moment
      .tz(`${rangeTarget[1]} 00:00:00`, "Asia/Tokyo")
      .add(1, "day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    const requestMode = String(request.mode || "").toLowerCase();
    let summary = null;
    let table = [];

    if (useAreaSnapshotTable) {
      const summarySql = createApprovedByAreaSummaryQuery({
        seasonId,
        gameFieldLayerId,
        snapshotEndDateJst: rangeTarget[1],
      });
      const shouldFetchRows = requestMode !== "summary";
      const rowsSql = shouldFetchRows
        ? createApprovedByAreaQuery({
            startUtc,
            endUtc,
            seasonId,
            gameFieldLayerId,
            snapshotEndDateJst: rangeTarget[1],
            useAreaSnapshotTable,
            searchText: request.searchText,
            rowsPerPage: request.rowsPerPage,
            sortDirection: request.sortDirection,
            applyTableLimit: requestMode === "trend",
          })
        : null;

      const fetchJobs = [
        call(
          callWithTimeoutAndRetry,
          getDataFromRDS,
          [summarySql, ENDPOINT.GET_DATA_RDS, jwtToken],
          RDS_HEAVY_RETRY
        ),
      ];

      if (shouldFetchRows && rowsSql) {
        fetchJobs.push(
          call(
            callWithTimeoutAndRetry,
            getDataFromRDS,
            [rowsSql, ENDPOINT.GET_DATA_RDS, jwtToken],
            RDS_HEAVY_RETRY
          )
        );
      }

      const results = yield call(runEffectsInBatches, fetchJobs);
      const summaryRows = parseRdsRows(
        results[0],
        REGULAR_SEASON_AREA_SNAPSHOT_SUMMARY_COLUMNS
      );
      const summaryRow = summaryRows[0] || {};
      summary = {
        registeredAreaCount: Number(summaryRow.registered_area_count) || 0,
        unshotAreaCount: Number(summaryRow.unshot_area_count) || 0,
        totalRegistered: Number(summaryRow.total_registered) || 0,
        totalReviewOk: Number(summaryRow.total_review_ok) || 0,
        totalAchievedPercent: Number(summaryRow.total_achieved_percent) || 0,
        totalParticipants: Number(summaryRow.total_participants) || 0,
      };

      if (shouldFetchRows && results[1]) {
        const rawRows = parseRdsRows(results[1], REGULAR_SEASON_AREA_SNAPSHOT_COLUMNS);
        table = rawRows.map((r) => {
          const registered = Number(r.count ?? r.registered_count) || 0;
          const reviewOk = Number(r.reviewok_count) || 0;
          const unshot = Math.max(registered - reviewOk, 0);
          const achieved = registered
            ? _.round((reviewOk / registered) * 100, 2)
            : 0;
          const participants = Number(r.participants_count) || 0;

          return {
            name:
              r.name ||
              r.medical_area_name ||
              r.city_name ||
              r.prefecture_name ||
              "",
            medicalAreaName: r.medical_area_name || null,
            medicalAreaId: r.medical_area_id || null,
            medicalAreaCode: r.medical_area_code || null,
            cityName: r.city_name || null,
            cityAreaId: r.city_area_id || null,
            cityAreaCode: r.city_area_code || null,
            prefectureName: r.prefecture_name || r.name || null,
            prefectureAreaId: r.prefecture_area_id || null,
            prefectureCode: r.prefecture_code || null,
            registered,
            unshot,
            reviewOk,
            achievedPercent: achieved,
            participants,
          };
        });
      }
    } else {
      const byAreaSql = createApprovedByAreaQuery({
        startUtc,
        endUtc,
        seasonId,
        gameFieldLayerId,
        snapshotEndDateJst: rangeTarget[1],
        useAreaSnapshotTable,
        searchText: request.searchText,
        rowsPerPage: request.rowsPerPage,
        sortDirection: request.sortDirection,
        applyTableLimit: false,
      });

      const fetchByAreaData = getDataFromSxi;
      const fetchByAreaEndpoint = ENDPOINT.GET_DATA_SXI_DENCHU;
      const rows = yield call(
        callWithTimeoutAndRetry,
        fetchByAreaData,
        [byAreaSql, fetchByAreaEndpoint, jwtToken],
        RDS_HEAVY_RETRY
      );

      const rawRows = Array.isArray(rows)
        ? rows
        : Array.isArray(rows?.body)
          ? rows.body
          : [];

      table = rawRows.map((r) => {
        const registered = Number(r.count ?? r.registered_count) || 0;
        const reviewOk = Number(r.reviewok_count) || 0;
        const unshot = Math.max(registered - reviewOk, 0);
        const achieved = registered
          ? _.round((reviewOk / registered) * 100, 2)
          : 0;
        const participants = Number(r.participants_count) || 0;

        return {
          name:
            r.name ||
            r.medical_area_name ||
            r.city_name ||
            r.prefecture_name ||
            "",
          medicalAreaName: r.medical_area_name || null,
          medicalAreaId: r.medical_area_id || null,
          medicalAreaCode: r.medical_area_code || null,
          cityName: r.city_name || null,
          cityAreaId: r.city_area_id || null,
          cityAreaCode: r.city_area_code || null,
          prefectureName: r.prefecture_name || r.name || null,
          prefectureAreaId: r.prefecture_area_id || null,
          prefectureCode: r.prefecture_code || null,
          registered,
          unshot,
          reviewOk,
          achievedPercent: achieved,
          participants,
        };
      });

      const totals = table.reduce(
        (acc, row) => {
          acc.totalRegistered += row.registered;
          acc.totalReviewOk += row.reviewOk;
          acc.totalUnshot += row.unshot;
          return acc;
        },
        { totalRegistered: 0, totalReviewOk: 0, totalUnshot: 0 }
      );

      const overallAchievedPercent = totals.totalRegistered
        ? _.round((totals.totalReviewOk / totals.totalRegistered) * 100, 2)
        : 0;

      const registeredAreaCount = table.length;
      const unshotAreaCount = table.filter((r) =>
        (Number(r.reviewOk) || 0) === 0 || Number(r.registered) === Number(r.unshot)
      ).length;
      const totalParticipants =
        rawRows.length > 0 ? Number(rawRows[0].total_participants) || 0 : 0;

      summary = {
        registeredAreaCount,
        unshotAreaCount,
        totalRegistered: totals.totalRegistered,
        totalReviewOk: totals.totalReviewOk,
        totalAchievedPercent: overallAchievedPercent,
        totalParticipants,
      };
    }

    const tableForMui = table.map((row, index) => {
      const showPrefectureColumn = [
        "regular_1_l2",
        "regular_1_l3",
      ].includes(gameFieldLayerId);

      if (showPrefectureColumn) {
        return [
          index + 1,                    // #
          row.name,                     // エリア名
          row.prefectureName || "",     // 都道府県
          row.registered,               // 登録数
          row.unshot,                   // 未撮影数
          row.reviewOk,                 // レビューOK撮影数
          row.achievedPercent,          // 達成率(%)
          row.participants,             // 参加者数
        ];
      }

      return [
        index + 1,                      // #
        row.name,                       // エリア名
        row.registered,                 // 登録数
        row.unshot,                     // 未撮影数
        row.reviewOk,                   // レビューOK撮影数
        row.achievedPercent,            // 達成率(%)
        row.participants,               // 参加者数
      ];
    });

    if (targetCompany === "tepco") {
      yield put(setByAreaDataSummaryTepco(summary));
      yield put(setByAreaDataTableTepco(tableForMui));
    } else if (targetCompany === "ntt") {
      yield put(setByAreaDataSummaryNtt(summary));
      yield put(setByAreaDataTableNtt(tableForMui));
      yield put(setByAreaDataRowsNtt(table));
    } else if (targetCompany === "ntt-ft") {
      yield put(setByAreaDataSummaryNttFt(summary));
      yield put(setByAreaDataTableNttFt(tableForMui));
    } else if (targetCompany === "hokuden") {
      yield put(setByAreaDataSummaryHokuden(summary));
      yield put(setByAreaDataTableHokuden(tableForMui));
    }
  } catch (err) {
    console.log("[getPictreeDataPhotoShootTrendByAsset] ERROR", err);
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export function* getByAreaTrendData(action) {
  try {
    yield put(setByAreaTrendDataLoadingNtt(true));
    yield put(
      setByAreaTrendDataNtt({
        date: [],
        series: [],
        topLegendNames: [],
      })
    );

    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRangeNtt);
    let selectedSeasonData = yield select(selectedSeasonNtt);
    const request =
      typeof action?.payload === "string"
        ? { company: action.payload }
        : action?.payload || {};
    const sortDirection = normalizeAreaTableSortDirection(request.sortDirection);

    if (!selectedSeasonData || selectedSeasonData.length === 0) {
      const seasonDataFallback = yield select(
        (state) => state.pictreeNtt.seasonData
      );
      if (seasonDataFallback && seasonDataFallback.length > 0) {
        selectedSeasonData = [seasonDataFallback[0]];
      }
    }

    if (!selectedSeasonData || selectedSeasonData.length === 0) {
      yield put(setByAreaTrendDataLoadingNtt(false));
      return;
    }

    const seasonId = selectedSeasonData[0].seasonId;
    if (!isRegularSeasonId(seasonId)) {
      yield put(setByAreaTrendDataLoadingNtt(false));
      return;
    }

    const gameFieldLayerId =
      (yield select(selectedAreaLayerIdNtt)) || "regular_1_l1";
    if (!["regular_1_l1", "regular_1_l2", "regular_1_l3"].includes(gameFieldLayerId)) {
      yield put(setByAreaTrendDataLoadingNtt(false));
      return;
    }

    if (!rangeTarget || rangeTarget.length < 2 || !rangeTarget[0] || !rangeTarget[1]) {
      yield put(setByAreaTrendDataLoadingNtt(false));
      return;
    }

    const trendSql = {
      query: `
        WITH ranked_rows AS (
          SELECT
            areaName AS area_name,
            snapshotDateJst AS snapshot_date_jst,
            achievedPercent AS achieved_percent,
            registeredCount AS registered_count,
            reviewOkCount AS reviewok_count,
            participantsCount AS participants_count,
            totalParticipants AS total_participants,
            prefectureName AS prefecture_name,
            CASE
              WHEN '${gameFieldLayerId}' = 'regular_1_l1' THEN areaName
              ELSE CONCAT(areaName, ' (', COALESCE(prefectureName, ''), ')')
            END AS series_name
          FROM ${REGULAR_SEASON_AREA_SNAPSHOT_TABLE}
          WHERE seasonId = '${seasonId}'
            AND companyCode = 'NTT_ME'
            AND areaLayerId = '${gameFieldLayerId}'
            AND snapshotDateJst >= '${rangeTarget[0]}'
            AND snapshotDateJst <= '${rangeTarget[1]}'
        ),
        candidate_series AS (
          SELECT series_name
          FROM ranked_rows
          WHERE snapshot_date_jst = '${rangeTarget[1]}'
          GROUP BY series_name
          ORDER BY MAX(achieved_percent) ${sortDirection}, MAX(registered_count) DESC, series_name ASC
          LIMIT ${REGULAR_AREA_TABLE_DEFAULT_LIMIT}
        )
        SELECT
          area_name,
          snapshot_date_jst,
          achieved_percent,
          registered_count,
          reviewok_count,
          participants_count,
          total_participants,
          prefecture_name
        FROM ranked_rows
        WHERE series_name IN (SELECT series_name FROM candidate_series)
        ORDER BY snapshot_date_jst ASC, achieved_percent ${sortDirection}, registered_count DESC, area_name ASC;
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const rows = yield call(
      callWithTimeoutAndRetry,
      getDataFromRDS,
      [trendSql, ENDPOINT.GET_DATA_RDS, jwtToken],
      RDS_HEAVY_RETRY
    );

    const rawRows = parseRdsRows(rows, REGULAR_SEASON_AREA_TREND_COLUMNS);
    const trendData = buildTrendPayload(
      rawRows,
      rangeTarget[0],
      rangeTarget[1],
      gameFieldLayerId,
      sortDirection,
    );

    yield put(setByAreaTrendDataNtt(trendData));
  } catch (err) {
    console.log("[getByAreaTrendData] ERROR", err);
    yield put(
      setByAreaTrendDataNtt({
        date: [],
        series: [],
        topLegendNames: [],
      })
    );
  } finally {
    yield put(setByAreaTrendDataLoadingNtt(false));
  }
}

export default function* watchPageSettings() {
  yield takeEvery(PICTREE.GET_BY_AREA_DATA, getByAreaData);
  yield takeEvery(PICTREE.GET_BY_AREA_TREND_DATA, getByAreaTrendData);
}
